<template>
  <div class="mirror-group-form">
    <drawer-dialog-controls
      :is-loading="isSaving"
      @close="$emit('close')"
      @save="onSave"
    >
      {{ group ? 'Edit Group' : 'Add New Group' }}
    </drawer-dialog-controls>

    <v-card-text>
      <v-text-field
        v-model="form.name"
        label="Group name"
        hide-details
        style="min-width: 220px; max-width: 220px"
      />

      <v-card class="mt-4">
        <v-expansion-panels v-model="geoPanel" flat>
          <v-expansion-panel>
            <v-expansion-panel-header class="mirror-group-form__expansion-header px-4">
              <div class="d-flex align-center">
                <span class="tab-title">Geo Rules</span>
                <v-tooltip right>
                  <template #activator="{ on }">
                    <v-icon small class="ml-2" v-on="on">mdi-information</v-icon>
                  </template>
                  <span>By default, the domain is available to all countries except those that are blocked</span>
                </v-tooltip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="mirror-group-form__expansion-content">
              <v-divider></v-divider>
              <div class="ma-4 mirror-group-form__expansion-content-wrapper">
                <v-simple-table dense class="">
                  <thead>
                    <tr class="geo-rules-table__row">
                      <th style="width: 216px" class="text-left subtitle-2 black--text">Geo Rule</th>
                      <th class="text-left subtitle-2 black--text">Value</th>
                      <th class="text-left subtitle-2 black--text">Additional</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr class="geo-rules-table__row" >
                      <td class="body-2">Default rule</td>
                      <td >
                        <v-select
                          v-model="form.settings.geo_settings.default_rule"
                          :items="defaultRuleOptions"
                          hide-details
                          class="ma-0 py-0"
                          style="max-width: 200px"
                        />
                      </td>
                      <td></td>
                    </tr>

                    <tr
                      v-if="isGeoRuleVisible('redirect')"
                      class="geo-rules-table__row"
                      @click="toggleGeoEditor('redirect')"
                    >
                      <td class="body-2">Redirect</td>
                      <td>
                        <lang-row
                          :countries="redirectRulesState"
                          type="outline"
                          color="grey"
                          :max-items="3"
                          class="auto-width ml-1"
                        />
                      </td>
                      <td class="body-2">
                        <div class="d-flex align-center justify-space-between">
                          <div class="d-flex align-center">
                            <v-icon small :color="redirectorName ? 'blue' : 'grey'">mdi-redo-variant</v-icon>
                            <span class="ml-1 caption" :class="redirectorName ? 'blue--text' : 'grey--text'">
                              Redirect → {{ redirectorName || 'none' }}
                            </span>
                          </div>
                          <v-btn icon x-small>
                            <v-icon small>{{ geoEditors.redirect ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="isGeoRuleVisible('redirect') && geoEditors.redirect">
                      <td colspan="3" class="py-4 ">
                        <div class="mb-2 font-weight-medium">Redirect GEO</div>
                        <div class="caption grey--text">Geo</div>
                        <select-countries
                          :chosenCountries="redirectRulesState"
                          :countries="allCountries"
                          :chip="true"
                          chip-color="#2196F3"
                          :outlined="false"
                          @input="(items) => redirectRulesState = items.map(({ value }) => value)"
                          class="select-countries"
                        />
                      </td>
                    </tr>

                    <tr v-if="isGeoRuleVisible('blocked')" class="geo-rules-table__row" @click="toggleGeoEditor('blocked')">
                      <td class="body-2">Blocked Geo</td>
                      <td>
                        <lang-row
                          :countries="getGeo(form).blockedDisplay"
                          type="outline"
                          color="error"
                          :max-items="3"
                          class="auto-width ml-1"
                        />
                      </td>
                      <td>
                        <div class="d-flex align-center justify-space-between">
                          <div class="d-flex align-center">
                            <v-icon small color="error">mdi-close-circle-outline</v-icon>
                            <span class="ml-1 caption">Deny (403)</span>
                          </div>
                          <v-btn icon x-small>
                            <v-icon small>{{ geoEditors.blocked ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="isGeoRuleVisible('blocked') && geoEditors.blocked">
                      <td colspan="3" class="py-4">
                        <geo-rules-editor
                          :rules="blockedRulesState"
                          @add="addGeoRule('blocked')"
                          @remove="removeGeoRule('blocked', $event)"
                          @update:geo="updateGeoRuleField('blocked', $event.index, 'geo', $event.value)"
                          @update:devices="updateGeoRuleField('blocked', $event.index, 'devices', $event.value)"
                        />
                      </td>
                    </tr>

                    <tr v-if="isGeoRuleVisible('allowed')" class="geo-rules-table__row">
                      <td class="body-2">Allowed Geo</td>
                      <td>
                        <lang-row
                          v-if="getGeo(form).allowedDisplay.length"
                          :countries="getGeo(form).allowedDisplay"
                          type="outline"
                          color="success"
                          :max-items="3"
                          class="auto-width ml-1"
                        />
                      </td>
                      <td class="py-2 body-2">
                        <div class="d-flex justify-end">
                          <v-btn icon x-small @click="toggleGeoEditor('allowed')">
                            <v-icon small>{{ geoEditors.allowed ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="isGeoRuleVisible('allowed') && geoEditors.allowed">
                      <td colspan="3" class="py-4">
                        <geo-rules-editor
                          :rules="allowedRulesState"
                          @add="addGeoRule('allowed')"
                          @remove="removeGeoRule('allowed', $event)"
                          @update:geo="updateGeoRuleField('allowed', $event.index, 'geo', $event.value)"
                          @update:devices="updateGeoRuleField('allowed', $event.index, 'devices', $event.value)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>



      <div class="mt-6">
        <div class="body-1 font-weight-medium text-uppercase">Mirrors in Group</div>
        <div class="caption grey--text text--darken-1 mb-3">Add mirrors to this group. One must be Primary</div>

        <div class="d-flex align-center justify-space-between mirrors__header black--text subtitle-2 mb-2">
          <div class="mirrors__col-primary">Primary</div>
          <div class="mirrors__col-mirror">Mirror</div>
          <div class="mirrors__col-geo">Redirector Geo</div>
          <div class="mirrors__col-geo">Language Locale</div>
          <div class="mirrors__col-geo">Blocked Geo</div>
          <div class="mirrors__col-status">Status</div>
          <div class="mirrors__col-delete"></div>
        </div>

        <div class="mirrors__row">
          <v-card
            v-for="(item, index) in mirrorRows"
            :key="index"
            outlined
            class="d-flex align-center mirrors__mirror-card"
          >
            <div class="mirrors__col-primary">
              <v-btn
                class="mirrors__col-primary-btn"
                :color="(form.primary_mirror_id === item.mirror_id && form.primary_mirror_id) ? 'orange' : 'grey lighten-2'"
                outlined
                @click="setPrimary(item.mirror_id)"
              >
                <v-icon :color="(form.primary_mirror_id === item.mirror_id && form.primary_mirror_id) ? 'orange' : 'grey lighten-2'">
                  mdi-crown
                </v-icon>
              </v-btn>
            </div>

            <div class="mirrors__col-mirror">
              <v-select
                v-model="item.mirror_id"
                :items="getAvailableMirrors(item.mirror_id)"
                item-text="text"
                item-value="value"
                dense
                outlined
                hide-details
                clearable
                color="grey"
                style="max-width: 195px"
              />
            </div>

            <div class="mirrors__col-geo">
              <lang-row
                :countries="getGeo(getMirrorById(item.mirror_id)).redirect"
                type="outline"
                color="grey"
                :max-items="3"
                class="auto-width ml-1"
              />
            </div>

            <div class="mirrors__col-geo">
              <lang-row
                :countries="mirrorLocales(getMirrorById(item.mirror_id))"
                type="outline"
                color="success"
                :max-items="3"
                :fill-first="true"
                class="auto-width ml-1"
              />
            </div>

            <div class="mirrors__col-geo">
              <lang-row
                :countries="getGeo(getMirrorById(item.mirror_id)).blockedDisplay"
                type="outline"
                color="error"
                :max-items="3"
                class="auto-width ml-1"
              />
            </div>

            <div class="mirrors__col-status">
              <v-chip
                v-if="getMirrorById(item.mirror_id)"
                outlined
                :color="statusColor(getMirrorById(item.mirror_id).status)"
                class="text-capitalize mirror-group-form__pill-chip"
              >
                {{ getMirrorById(item.mirror_id).status }}
              </v-chip>
            </div>

            <div class="mirrors__col-delete">
              <v-btn icon small color="error" @click="removeMirrorRow(index)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </v-card>
        </div>


        <v-btn
          outlined
          small
          color="success"
          class="mt-3"
          @click="addMirrorRow"
        >
          Add Mirror
        </v-btn>
      </div>
    </v-card-text>
  </div>
</template>

<script>
  import { cloneDeep, merge } from 'lodash'
  import { DrawerDialogControls } from '@/components/core/DrawerDialog'
  import LangRow from '@/components/core/SelectLang/LangRow/LangRow.vue'
  import SelectCountries from '@/components/core/SelectCountries.vue'
  import { allCountries } from '@/components/core/SelectLang/mapperIsoLocale'
  import { DEFAULT_GEO_RULE_OPTIONS } from '@/modules/mirrors-redirectors/constants/const'
  import mirrorMixin from '@/modules/mirrors-redirectors/mixins/mirror'
  import GeoRulesEditor from './GeoRulesEditor.vue'

  const SKELETON_MIRROR_GROUP = {
    entity_id: null,
    group_mirror_id: null,
    name: '',
    primary_mirror_id: null,
    mirrors: { items: [] },
    settings: {
      geo_settings: {
        default_rule: 'allow',
        blocked_geo: [],
        allowed_geo: [],
        redirect: [],
      },
    },
  }

  const GEO_RULES_MAP = {
    blocked: { field: 'blocked_geo', stateKey: 'blockedRulesState' },
    allowed: { field: 'allowed_geo', stateKey: 'allowedRulesState' },
  }

  export default {
    name: 'MirrorGroupForm',
    mixins: [mirrorMixin],
    components: {
      DrawerDialogControls,
      LangRow,
      SelectCountries,
      GeoRulesEditor,
    },
    props: {
      group: {
        type: Object,
        default: null,
      },
      redirectorName: {
        type: String,
        default: '',
      },
      mirrors: {
        type: Array,
        default: () => [],
      },
      project: {
        type: Number,
        default: null,
      },
      project_config: {
        type: Object,
        default: null,
      },
      isSaving: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        form: cloneDeep(SKELETON_MIRROR_GROUP),
        mirrorRows: [],
        blockedRulesState: [],
        allowedRulesState: [],
        redirectRulesState: [],
        allCountries,
        geoPanel: 0,
        geoEditors: {
          redirect: false,
          blocked: false,
          allowed: false,
        },
        defaultRuleOptions: DEFAULT_GEO_RULE_OPTIONS,
      }
    },
    computed: {
      defaultRule() {
        return this.form.settings.geo_settings.default_rule
      },
      mirrorSelectItems() {
        return (this.mirrors || []).map((m) => ({
          text: m.name,
          value: m.mirror_id,
        }))
      },
    },
    watch: {
      group: {
        immediate: true,
        deep: true,
        handler(group) {
          this.initFromGroup(group)
          this.selected = []
        },
      },
      mirrorRows: {
        deep: true,
        handler(rows) {
          this.form.mirrors.items = rows.map((r) => r.mirror_id).filter(Boolean)
        },
      },
      defaultRule() {
        const keys = Object.keys(this.geoEditors)
        for (let i = 0; i < keys.length; i += 1) {
          this.geoEditors[keys[i]] = false
        }
      },
    },
    methods: {
      isGeoRuleVisible(ruleType) {
        const visibilityByRule = {
          allow: { redirect: true, blocked: true, allowed: false },
          deny: { redirect: true, blocked: false, allowed: true },
          redir: { redirect: false, blocked: true, allowed: true },
        }
        return visibilityByRule[this.defaultRule]?.[ruleType] ?? false
      },
      getNewSkeleton(group) {
        const base = cloneDeep(SKELETON_MIRROR_GROUP)
        if (!group) {
          return {
            ...base,
            entity_id: this.project,
          }
        }
        return merge(base, {
          entity_id: group.entity_id,
          group_mirror_id: group.group_mirror_id,
          name: group.name || '',
          primary_mirror_id: group.primary_mirror_id ?? null,
          mirrors: {
            items: (group.mirrors || []).map((m) => m.mirror_id),
          },
          settings: cloneDeep(group.settings || {}),
        })
      },
      initFromGroup(group) {
        const nextForm = this.getNewSkeleton(group)
        this.form = nextForm

        const rows = (group?.mirrors || []).map((m) => ({ mirror_id: m.mirror_id }))
        this.mirrorRows = rows.length ? rows : [{ mirror_id: null }]
        this.blockedRulesState = this.parseGeoList(nextForm.settings.geo_settings.blocked_geo)
        this.allowedRulesState = this.parseGeoList(nextForm.settings.geo_settings.allowed_geo)
        this.redirectRulesState = nextForm.settings.geo_settings.redirect ?? []
      },

      toggleGeoEditor(key) {
        this.geoEditors[key] = !this.geoEditors[key]
      },

      addGeoRule(key) {
        this[GEO_RULES_MAP[key].stateKey].push({ geo: null, devices: [] })
      },

      removeGeoRule(key, index) {
        this[GEO_RULES_MAP[key].stateKey].splice(index, 1)
      },

      updateGeoRuleField(key, index, prop, value) {
        const state = this[GEO_RULES_MAP[key].stateKey]
        if (state[index]) {
          state[index][prop] = value
        }
      },

      getMirrorById(id) {
        if (!id) return null
        return (this.mirrors || []).find((m) => m.mirror_id === id) || null
      },

      getAvailableMirrors(currentMirrorId) {
        const selectedMirrorIds = new Set(
          this.mirrorRows
            .map((row) => row.mirror_id)
            .filter((mirrorId) => mirrorId != null && mirrorId !== currentMirrorId)
        )

        return this.mirrorSelectItems.filter(
          (item) => !selectedMirrorIds.has(item.value)
        )
      },

      getGeo(scope) {
        return this.getGeoForEditableForm({
          scope,
          form: this.form,
          blockedRulesState: this.blockedRulesState,
          allowedRulesState: this.allowedRulesState,
          redirectRulesState: this.redirectRulesState,
        })
      },

      setPrimary(mirrorId) {
        const current  = this.form.primary_mirror_id
        this.form.primary_mirror_id = mirrorId !== current ? mirrorId : null
      },

      addMirrorRow() {
        this.mirrorRows.push({ mirror_id: null })
      },

      removeMirrorRow(index) {
        const [{ mirror_id }] = this.mirrorRows.splice(index, 1)
        if (mirror_id && this.form.primary_mirror_id === mirror_id) {
          this.form.primary_mirror_id = null
        }
      },

      onSave() {
        const group = cloneDeep(this.form)
        group.settings = {
          ...group.settings,
          geo_settings: {
            ...group.settings?.geo_settings,
            blocked_geo: this.formatGeoList(this.blockedRulesState),
            allowed_geo: this.formatGeoList(this.allowedRulesState),
            redirect: this.redirectRulesState.filter(Boolean),
          },
        }
        this.$emit('save', { group })
      },
    },
  }
</script>

<style lang="scss" scoped>
  .mirror-group-form {

    &__pill-chip {
      height: 24px;
      min-height: 24px;
      padding: 5px 8px;
      border-radius: 50px;
      font-size: 12px;
    }

    &__expansion {
      .tab-title {
        font-size: 18px;
      }

      &-header {
        min-height: 56px !important;
      }
      &-content {
        ::v-deep .v-expansion-panel-content__wrap {
          padding: 0;
        }

        &-wrapper {
          border: 1px solid #D9D9D9;
          border-radius: 4px;
        }
      }
    }

    .mirrors  {
      &__header {
        gap: 16px;
      }

      &__row {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      &__mirror-card {
        display: flex;
        align-items: center;justify-content: space-between;
        gap: 16px;
        min-height: 74px;
        border-radius: 4px;
      }

      &__col-primary {
        width: 64px;
        min-width: 64px;
        display: flex;
        align-items: center;
        justify-content: center;

        &-btn {
          min-width: 44px;
          height: 44px;
          padding: 0;

        }
      }

      &__col-mirror {
        width: 195px;
        min-width: 195px;

        & ::v-deep fieldset {
          color: #D9D9D9 !important;
          border: 1px solid #D9D9D9;
        }
      }


      &__col-geo {
        width: 150px;
        min-width: 150px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &__col-status {
        width: 90px;
        min-width: 90px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &__col-delete {
        width: 40px;
        min-width: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

  }

  .select-countries {
    ::v-deep .custom-select {
      margin-left: 0;
      max-width: 548px;
    }
  }

  .geo-rules-table {
    &__row {
      height: 48px;
    }
  }

  .auto-width {
    width: auto !important;
  }

</style>
